<template>
  <!-- A subscriber recognized via subscribe-form submit is not authenticated
       (decision 0165: typing an email is recognition, not proof of identity).
       Instead of treating them as logged in, offer a magic link to actually
       sign in before they can manage their subscription. -->
  <div class="recognized-sign-in">
    <span class="confirmation-email-message" v-html="subscribedViaMessage" />

    <div v-if="status === 'success'" class="magic-link-success-message">
      {{ t("web.subscription.message.magic_link_sent") }}
    </div>

    <template v-else>
      <CaptchaChallenge
        v-if="captchaRequired"
        :newsletter="newsletter"
        @callback="handleCaptchaToken"
        @expired-callback="setCaptchaToken('')"
        @error-callback="onCaptchaError"
      />
      <ActionButton
        v-else
        :text="
          status === 'pending'
            ? t('web.subscription.action.sending_sign_in_link')
            : t('web.subscription.action.send_sign_in_link')
        "
        color="blue"
        :disabled="status === 'pending'"
        :pending="status === 'pending'"
        @click="send(emailAddress)"
      />

      <div v-if="status === 'error'" class="magic-link-error-message">
        {{ message }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "../../lib/utils";
import { useMagicLink } from "../composables/useMagicLink";
import ActionButton from "./ActionButton.vue";
import CaptchaChallenge from "./CaptchaChallenge.vue";

const props = defineProps<{
  newsletter: Newsletter;
  emailAddress: string;
}>();

const t = createTranslator(() => props.newsletter.locale);

const {
  status,
  message,
  captchaRequired,
  send,
  setCaptchaToken,
  handleCaptchaError,
} = useMagicLink();

function handleCaptchaToken(token: string) {
  setCaptchaToken(token);
  send(props.emailAddress);
}

function onCaptchaError() {
  handleCaptchaError(t("web.subscription.error.captcha_failed"));
}

const escapeHtml = (str: string) =>
  str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const subscribedViaMessage = computed(() =>
  t("web.subscription.message.subscribed_via")
    .replace(
      "{newsletter}",
      `<strong>${escapeHtml(props.newsletter.name)}</strong>`
    )
    .replace("{email}", `<strong>${escapeHtml(props.emailAddress)}</strong>`)
);
</script>
